import React, { useState } from 'react';
import { IconButton, Popover, Slider, Button, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { panelTheme } from '../PanelComerciales.theme';

interface CapacityEditorProps {
    value: number;
    onChange: (next: number) => void;
    min?: number;
    max?: number;
}

// Tope del boost alineado con el backend (SellerConfigPatch.capacity_pct
// le=300, decisión 2026-07): hasta triplicar la capacidad estimada.
const CAPACITY_BOOST_MAX = 300;

const CapacityEditor: React.FC<CapacityEditorProps> = ({
    value,
    onChange,
    min = 0,
    max = CAPACITY_BOOST_MAX,
}) => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [draft, setDraft] = useState(value);

    const open = Boolean(anchor);

    const openEditor = (e: React.MouseEvent<HTMLElement>) => {
        setDraft(value);
        setAnchor(e.currentTarget);
    };

    const close = () => setAnchor(null);

    const apply = () => {
        const clamped = Math.max(min, Math.min(max, Math.round(draft)));
        onChange(clamped);
        close();
    };

    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <strong style={{ color: panelTheme.colors.text }}>{value}%</strong>
            <IconButton size='small' onClick={openEditor}>
                <EditOutlinedIcon fontSize='inherit' />
            </IconButton>
            <Popover
                open={open}
                anchorEl={anchor}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        sx: {
                            p: 2,
                            width: 220,
                            borderRadius: panelTheme.radius.md,
                        },
                    },
                }}
            >
                <Typography variant='caption' sx={{ color: panelTheme.colors.textMuted }}>
                    Ajustar capacidad ({min}–{max}%)
                </Typography>
                <Slider
                    size='small'
                    value={draft}
                    onChange={(_, v) => setDraft(Array.isArray(v) ? v[0] : v)}
                    min={min}
                    max={max}
                    step={5}
                    valueLabelDisplay='auto'
                    sx={{ color: panelTheme.colors.indigo, mt: 1 }}
                />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <Button size='small' onClick={close} sx={{ textTransform: 'none' }}>
                        Cancelar
                    </Button>
                    <Button
                        size='small'
                        variant='contained'
                        onClick={apply}
                        sx={{
                            textTransform: 'none',
                            background: panelTheme.colors.indigo,
                            '&:hover': { background: panelTheme.colors.violet },
                        }}
                    >
                        Aplicar
                    </Button>
                </div>
            </Popover>
        </span>
    );
};

export default CapacityEditor;
